/**
 * features/mailbox/mock.ts — deterministic fixtures for the delegated-mailbox UI
 * (Story MC6). Mock data-mode (dev + vitest default) resolves these so every
 * screen/state renders standalone with ZERO network.
 *
 * The DEFAULT (`MOCK_MAILBOX_CONNECTIONS`) is one CONNECTED Microsoft mailbox +
 * `connect_enabled:true` — so the Kommunikation mock list keeps rendering (there
 * IS a connection → no zero-state → the existing T5/UI-U9 mock screens are
 * unchanged). The empty + disabled variants drive the zero-state / coming-soon
 * states in their own tests.
 */

import type { MailboxConnectionDto, MailboxConnectionsResponse, ManualSyncResult } from './types';

/** Primary connected Microsoft mailbox (the send-capable happy path). */
export const MOCK_MS_CONNECTION: MailboxConnectionDto = {
  id: 'mbx-ms-1',
  provider: 'microsoft',
  email: 'anna@makler.ch',
  status: 'connected',
  scopes: ['Mail.Read', 'Mail.Send'],
  connectedAt: '2026-07-20T08:15:00.000Z',
  lastSyncAt: '2026-07-29T06:40:00.000Z',
  lastError: null,
};

/** Default: available (enabled) + one connected mailbox. */
export const MOCK_MAILBOX_CONNECTIONS: MailboxConnectionsResponse = {
  connect_enabled: true,
  providers: ['microsoft', 'google'],
  connections: [MOCK_MS_CONNECTION],
};

/** Available (enabled) + ZERO connections → the ⭐ Kommunikation zero-state. */
export const MOCK_MAILBOX_CONNECTIONS_EMPTY: MailboxConnectionsResponse = {
  connect_enabled: true,
  providers: ['microsoft', 'google'],
  connections: [],
};

/** connect disabled (creds not seeded / MAILBOX_CONNECT_ENABLED=false) → "in Kürze verfügbar". */
export const MOCK_MAILBOX_CONNECTIONS_DISABLED: MailboxConnectionsResponse = {
  connect_enabled: false,
  providers: ['microsoft', 'google'],
  connections: [],
};

/**
 * Manual "Jetzt synchronisieren" in mock mode (P2 hardening 06): resolve an empty round after
 * ~500 ms so the button's pending/spinner state is exercised with ZERO network.
 */
export function mockSyncNow(id: string): Promise<ManualSyncResult> {
  return new Promise((resolve) =>
    setTimeout(
      () =>
        resolve({
          connection_id: id,
          status: 'connected',
          ingested: 0,
          deduped: 0,
          blocked: 0,
          has_more: false,
          last_sync_at: new Date().toISOString(),
          error: null,
        }),
      500,
    ),
  );
}
